// ---- Cinematic timeline (plan 26) ----
// THE pure playback core for cinematics: the shot/action/dialogue state machine
// behind both the game client and the studio #/cinematics player. Deterministic
// by construction — no THREE, no DOM, no Date.now()/rng; time only enters as the
// dt passed to tick(), so `jumpToShot`/`duration` can fast-forward by simulation
// and land byte-identical state. Pinned by src/cinematic/timeline.test.ts.
//
// THE SEQUENCING CONTRACT lives in the CinematicSchema header (schemas.ts) — the
// five rules there are what this module implements; change them in both places
// or not at all. Blocking defaults are ONE function: actionBlocks() (validate.ts
// imports it so the "shot needs duration or a blocking action" check can't drift).

import type { CinematicDoc, CineAction, CineCamera, CineShot } from "../content/schemas.ts";
import { WALK_SPEED, RUN_SPEED } from "../shared.ts";
import { lerpAngle } from "../interp.ts";

/** Client-visual pacing tunables (dialogue streaming). Mutable like BLEND_TUNING —
 *  presentation only, never gameplay. */
export const CINE_TUNING = {
  charsPerSec: 28, // stream rate (per-say `rate` overrides)
  punctDwell: 3, // punctuation chars cost this × a normal char (breathing room)
  holdBase: 0.9, // post-stream hold = clamp(base + perChar·len, min, max) seconds
  holdPerChar: 0.035,
  holdMin: 1.2,
  holdMax: 4.5,
  faceEase: 6, // face-action turn ease rate (min(1, dt·k))
  defaultClipSec: 0.8, // gesture length when the host passes no clipDuration
};

const PUNCT = /[.,!?;:—…]/;

/** Blocking defaults — the one home (contract): say/wait ALWAYS block; face/pose
 *  don't unless asked; walk/gesture/fade block unless opted out. */
export function actionBlocks(a: CineAction): boolean {
  if (a.do === "say" || a.do === "wait") return true;
  if (a.do === "face" || a.do === "pose") return a.blocking ?? false;
  return a.blocking ?? true;
}

/** Post-stream hold time for a line of `len` chars (exported for the test). */
export function lineHold(len: number): number {
  const t = CINE_TUNING.holdBase + CINE_TUNING.holdPerChar * len;
  return Math.min(CINE_TUNING.holdMax, Math.max(CINE_TUNING.holdMin, t));
}

export type ActorPose = {
  x: number;
  z: number;
  ry: number;
  moving: boolean;
  running: boolean;
  /** Set on exactly the tick a gesture starts — the stage consumes it. */
  gesture?: string;
  /** Current held pose (poses.ts key), PERSISTENT across ticks until a `pose`
   *  action changes it — the stage layers it over the idle. From actor.pose. */
  pose?: string;
};

export type CineLine = { actor: string; text: string; visible: number; complete: boolean };
export type CineFade = { dir: "out" | "in"; ms: number };

export type TimelineOut = {
  shotIndex: number;
  shotT: number;
  done: boolean;
  camera: CineCamera;
  /** True exactly on the first tick of a shot whose transition is "cut" (and on
   *  the very first tick overall — there is nothing to ease from). */
  cameraCut: boolean;
  actors: ReadonlyMap<string, ActorPose>;
  line: CineLine | null;
  /** Fade events fired THIS tick (consume immediately). */
  fades: CineFade[];
};

export type Timeline = {
  tick(dt: number): TimelineOut;
  /** Streaming line → complete it; completed line → advance the beat; no line →
   *  end the current shot. Always allowed (fast-reader fast-forward). */
  click(): void;
  /** Honors doc.skippable; returns true when the cinematic ended. */
  skip(): boolean;
  /** Deterministic fast-forward to the start of shot i (studio scrub). */
  jumpToShot(i: number): void;
  /** Estimated full runtime in seconds (simulated; capped). */
  duration(): number;
};

export type TimelineOpts = {
  /** Real gesture-clip lengths (the host reads anim clips); default fixed. */
  clipDuration?: (clip: string) => number;
};

// ---------------------------------------------------------------------------

type WalkRt = {
  pts: { x: number; z: number }[]; // waypoints INCLUDING the start point
  seg: number; // current segment index (pts[seg] → pts[seg+1])
  segDist: number; // distance travelled into the current segment
  speed: number;
  running: boolean;
  done: boolean;
};

type ActorRt = ActorPose & {
  walk: WalkRt | null;
  faceTarget: { actor: string } | { ry: number } | null;
};

type SayRt = { a: Extract<CineAction, { do: "say" }>; charEnds: number[]; total: number };

type ActRt = {
  a: CineAction;
  blocking: boolean;
  started: boolean;
  finished: boolean;
  /** Absolute shot time this action ends (wait/gesture/blocking fade). */
  endT: number;
  /** The walk runtime this action created (finished when it completes). */
  walk: WalkRt | null;
};

type ShotRt = {
  acts: ActRt[]; // everything except says
  says: SayRt[]; // the queue (doc order)
  sayIx: number;
  sayT: number; // stream time into the current say
  sayHold: number; // remaining hold once streamed (<0 = still streaming)
  sayStarted: boolean;
};

function isPunctCost(ch: string): number {
  return PUNCT.test(ch) ? CINE_TUNING.punctDwell : 1;
}

/** Per-char cumulative stream end-times for a line at `rate` chars/sec. */
function charEnds(text: string, rate: number): number[] {
  const per = 1 / rate;
  const ends: number[] = [];
  let t = 0;
  for (const ch of text) {
    t += per * isPunctCost(ch);
    ends.push(t);
  }
  return ends;
}

/** Wrap a signed angle to [-π, π] — delegates to the shared shortest-path math. */
const shortAngle = (a: number): number => lerpAngle(0, a, 1);

export const DUR_CAP = 600; // duration()/jump simulation safety cap (seconds)
const SIM_DT = 1 / 30;

export function makeTimeline(doc: CinematicDoc, opts: TimelineOpts = {}): Timeline {
  const clipDuration = opts.clipDuration ?? (() => CINE_TUNING.defaultClipSec);

  let shotIndex = 0;
  let shotT = 0;
  let done = false;
  let firstTick = true;
  let pendingCut = false;
  const actors = new Map<string, ActorRt>();
  let shot: ShotRt | null = null;
  const out: TimelineOut = {
    shotIndex: 0,
    shotT: 0,
    done: false,
    camera: doc.shots[0]!.camera,
    cameraCut: true,
    actors,
    line: null,
    fades: [],
  };
  // Reused per-tick line scratch (the `out`/`fades` discipline) — consumers read
  // it synchronously each tick, never retain it.
  const lineOut: CineLine = { actor: "", text: "", visible: 0, complete: false };

  function reset(): void {
    shotIndex = 0;
    shotT = 0;
    done = false;
    firstTick = true;
    pendingCut = false;
    actors.clear();
    for (const a of doc.actors) {
      actors.set(a.id, {
        x: a.at[0],
        z: a.at[1],
        ry: a.face ?? 0,
        moving: false,
        running: false,
        pose: a.pose,
        walk: null,
        faceTarget: null,
      });
    }
    shot = enterShot(doc.shots[0]!);
    out.line = null;
    out.fades.length = 0;
  }

  function enterShot(s: CineShot): ShotRt {
    const acts: ActRt[] = [];
    const says: SayRt[] = [];
    for (const a of s.actions ?? []) {
      if (a.do === "say") {
        says.push({ a, charEnds: charEnds(a.text, a.rate ?? CINE_TUNING.charsPerSec), total: 0 });
        const se = says[says.length - 1]!;
        se.total = se.charEnds[se.charEnds.length - 1] ?? 0;
      } else {
        acts.push({ a, blocking: actionBlocks(a), started: false, finished: false, endT: 0, walk: null });
      }
    }
    return { acts, says, sayIx: 0, sayT: 0, sayHold: -1, sayStarted: false };
  }

  /** Start any not-yet-started actions whose delay has arrived. */
  function startDue(): void {
    for (const act of shot!.acts) {
      if (act.started || shotT < (("delay" in act.a && act.a.delay) || 0)) continue;
      act.started = true;
      const a = act.a;
      const rt = "actor" in a ? actors.get(a.actor) : undefined;
      if (a.do === "walk" && rt) {
        const pts = [{ x: rt.x, z: rt.z }, ...a.path.map(([x, z]) => ({ x, z }))];
        const running = a.gait === "run";
        act.walk = { pts, seg: 0, segDist: 0, speed: running ? RUN_SPEED : WALK_SPEED, running, done: false };
        rt.walk = act.walk;
        rt.faceTarget = null; // travel facing wins while walking (contract rule 5)
      } else if (a.do === "face" && rt) {
        rt.faceTarget = typeof a.toward === "string" ? { actor: a.toward } : { ry: a.toward };
      } else if (a.do === "gesture" && rt) {
        rt.gesture = a.clip;
        act.endT = shotT + clipDuration(a.clip);
      } else if (a.do === "pose" && rt) {
        rt.pose = a.to; // persists until the next pose action (not cleared per tick)
        act.endT = shotT; // instant — settleActs marks it finished immediately
      } else if (a.do === "wait") {
        act.endT = shotT + a.seconds;
      } else if (a.do === "fade") {
        const ms = a.ms ?? 250;
        out.fades.push({ dir: a.dir, ms });
        act.endT = shotT + ms / 1000;
      }
    }
  }

  function advanceWalks(dt: number): void {
    for (const rt of actors.values()) {
      const w = rt.walk;
      rt.moving = false;
      rt.running = false;
      if (!w || w.done) continue;
      let travel = w.speed * dt;
      while (travel > 0 && w.seg < w.pts.length - 1) {
        const a = w.pts[w.seg]!;
        const b = w.pts[w.seg + 1]!;
        const segLen = Math.hypot(b.x - a.x, b.z - a.z);
        const remain = segLen - w.segDist;
        if (travel < remain) {
          w.segDist += travel;
          travel = 0;
        } else {
          travel -= remain;
          w.seg++;
          w.segDist = 0;
        }
      }
      if (w.seg >= w.pts.length - 1) {
        const last = w.pts[w.pts.length - 1]!;
        rt.x = last.x;
        rt.z = last.z;
        w.done = true;
        rt.walk = null;
      } else {
        const a = w.pts[w.seg]!;
        const b = w.pts[w.seg + 1]!;
        const segLen = Math.hypot(b.x - a.x, b.z - a.z) || 1;
        const f = w.segDist / segLen;
        rt.x = a.x + (b.x - a.x) * f;
        rt.z = a.z + (b.z - a.z) * f;
        rt.ry = Math.atan2(b.x - a.x, b.z - a.z);
        rt.moving = true;
        rt.running = w.running;
      }
    }
  }

  /** The ry that would point rt at `other`. */
  function towardRy(rt: ActorRt, other: ActorRt): number {
    return Math.atan2(other.x - rt.x, other.z - rt.z);
  }

  function faceTargetRy(rt: ActorRt): number | null {
    if (!rt.faceTarget) return null;
    if ("ry" in rt.faceTarget) return rt.faceTarget.ry;
    const other = actors.get(rt.faceTarget.actor);
    return other ? towardRy(rt, other) : null;
  }

  function advanceFacing(dt: number): void {
    for (const rt of actors.values()) {
      if (rt.walk) continue; // an explicit face waits out the walk (rule 5)
      const target = faceTargetRy(rt);
      if (target === null) continue;
      rt.ry = lerpAngle(rt.ry, target, Math.min(1, dt * CINE_TUNING.faceEase));
    }
  }

  /** Advance the say queue; returns the current visible line (or null). */
  function advanceSays(dt: number): CineLine | null {
    const s = shot!;
    if (s.sayIx >= s.says.length) return null;
    const say = s.says[s.sayIx]!;
    const delay = say.a.delay ?? 0;
    if (!s.sayStarted) {
      // A say waits for its own delay (earliest-start), then the queue's turn.
      if (shotT < delay) return null;
      s.sayStarted = true;
      s.sayT = 0;
      s.sayHold = -1;
    }
    if (s.sayHold < 0) {
      s.sayT += dt;
      if (s.sayT >= say.total) s.sayHold = lineHold(say.a.text.length);
    } else {
      s.sayHold -= dt;
      if (s.sayHold <= 0) {
        s.sayIx++;
        s.sayStarted = false;
        return advanceSays(0); // next line may begin this tick (visible 0)
      }
    }
    const visible =
      s.sayHold >= 0 ? say.charEnds.length : say.charEnds.findIndex((t) => t > s.sayT);
    const vis = visible < 0 ? say.charEnds.length : visible;
    lineOut.actor = say.a.actor;
    lineOut.text = say.a.text;
    lineOut.visible = vis;
    lineOut.complete = vis >= say.charEnds.length;
    return lineOut;
  }

  function blockingDone(): boolean {
    const s = shot!;
    if (s.sayIx < s.says.length) return false;
    for (const act of s.acts) {
      if (!act.blocking || act.finished) continue;
      return false;
    }
    return true;
  }

  function settleActs(): void {
    // Mark non-walk timed actions finished; walks finish when their runtime does.
    for (const act of shot!.acts) {
      if (!act.started || act.finished) continue;
      if (act.a.do === "walk") {
        if (act.walk?.done) act.finished = true;
      } else if (act.a.do === "face") {
        const rt = actors.get(act.a.actor);
        if (rt && !rt.walk && rt.faceTarget) {
          // face is "finished" once the turn has effectively settled
          const target = faceTargetRy(rt);
          if (target !== null && Math.abs(shortAngle(target - rt.ry)) < 0.05) act.finished = true;
        } else if (rt && !rt.faceTarget) act.finished = true;
      } else if (shotT >= act.endT) {
        act.finished = true;
      }
    }
  }

  /** End the current shot (natural or forced): contract rule 3/4 cleanup. */
  function endShot(): void {
    const s = shot!;
    // Unfinished BLOCKING walks snap to their final waypoint; non-blocking walks
    // persist across the cut (rule 4).
    for (const act of s.acts) {
      if (act.a.do === "walk" && act.blocking && act.walk && !act.walk.done) {
        const rt = actors.get((act.a as { actor: string }).actor);
        const last = act.walk.pts[act.walk.pts.length - 1]!;
        if (rt) {
          rt.x = last.x;
          rt.z = last.z;
          rt.walk = null;
        }
        act.walk.done = true;
      }
      if (act.a.do === "face" && act.started && !act.finished) {
        const rt = actors.get((act.a as { actor: string }).actor);
        if (rt) {
          const target = faceTargetRy(rt);
          if (target !== null) rt.ry = target;
          rt.faceTarget = null;
        }
      }
    }
    out.line = null;
    if (shotIndex + 1 >= doc.shots.length) {
      done = true;
      return;
    }
    shotIndex++;
    shotT = 0;
    shot = enterShot(doc.shots[shotIndex]!);
    pendingCut = (doc.shots[shotIndex]!.camera.transition ?? "ease") === "cut";
  }

  function tick(dt: number): TimelineOut {
    out.fades.length = 0;
    for (const rt of actors.values()) rt.gesture = undefined;
    if (done) {
      out.done = true;
      out.line = null;
      return out;
    }

    const cut = firstTick || pendingCut;
    firstTick = false;
    pendingCut = false;

    shotT += dt;
    startDue();
    advanceWalks(dt);
    advanceFacing(dt);
    const line = advanceSays(dt);
    settleActs();

    const s = doc.shots[shotIndex]!;
    const durUp = s.duration !== undefined && shotT >= s.duration;
    out.shotIndex = shotIndex;
    out.shotT = shotT;
    out.camera = s.camera;
    out.cameraCut = cut;
    out.line = line;
    out.done = false;

    if (durUp || (s.duration === undefined && blockingDone())) endShot();
    out.done = done;
    return out;
  }

  function click(): void {
    if (done || !shot) return;
    const s = shot;
    if (s.sayIx < s.says.length) {
      if (!s.sayStarted) {
        // A line is queued but hasn't begun (its `delay` hasn't elapsed, or a
        // prior click just advanced the queue): fast-forward TO it rather than
        // ending the shot over it — endShot() would silently drop the line.
        s.sayStarted = true;
        s.sayT = 0;
        s.sayHold = -1;
        return;
      }
      const say = s.says[s.sayIx]!;
      if (s.sayHold < 0) {
        // streaming → complete the line instantly (hold still applies)
        s.sayT = say.total;
        s.sayHold = lineHold(say.a.text.length);
      } else {
        // completed → advance the beat now
        s.sayIx++;
        s.sayStarted = false;
      }
      return;
    }
    // No line queued → advance to the next shot.
    endShot();
  }

  function skip(): boolean {
    if (!doc.skippable) return false;
    done = true;
    return true;
  }

  function jumpToShot(i: number): void {
    const target = Math.max(0, Math.min(i, doc.shots.length - 1));
    reset();
    let guard = DUR_CAP / SIM_DT;
    while (shotIndex < target && !done && guard-- > 0) tick(SIM_DT);
  }

  function duration(): number {
    // Simulate on a scratch instance so live state is untouched.
    const sim = makeTimeline(doc, opts);
    let t = 0;
    while (t < DUR_CAP) {
      if (sim.tick(SIM_DT).done) break;
      t += SIM_DT;
    }
    return t;
  }

  reset();
  return { tick, click, skip, jumpToShot, duration };
}
